import {gql} from '@apollo/client';
import React from 'react';

import DataConnector from 'connect/DataConnector';
import {StringIDFolder} from 'constants/FolderConstants';
import * as NavConstants from 'constants/NavConstants';
import {RegistrySummary} from 'constants/RegistryConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import * as SidepanelReduxActions from 'nav/sidepanel/SidepanelReduxActions';
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import notifications from 'notifications';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {StorablesSidepanelQuery} from './__generated/StorablesSidepanelQuery';
import RegistriesListing from './RegistriesListing';
import StorablesListing from './StorablesListing';
import {getDefaultStorablesSearchParams} from './StorablesSearchParams';

export const STORABLES_SIDEPANEL_QUERY = gql`
  query StorablesSidepanelQuery($registryId: StrictString!) @skipAudit @engComponent(name: SIGHTGLASS) {
    registry(id: $registryId) {
      ... on RegistryNode {
        ...NavStoreRegistryGraphQL
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('NavStoreRegistry').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface StorablesSidepanelProps {
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  isExpanded: boolean;
  activeItemId: string | null;
}

interface EnhancedProps {
  registryId: string | null;
  onListAllSources: () => void;
  onListRegistryFolder: (rootFolder: StringIDFolder) => void;
}

class StorablesSidepanelConnector extends DataConnector<StorablesSidepanelProps, EnhancedProps> {
  override stores = [SidepanelStore];

  _handleListRegistryFolder = (rootFolder: StringIDFolder): void => {
    const registry = {...(rootFolder.source as RegistrySummary), rootFolder};
    SidepanelActions.setStorablesSidepanelActiveRegistry(registry.id);
    this.dispatch(
      SidepanelReduxActions.openListing(
        NavConstants.Section.STORAGE,
        getDefaultStorablesSearchParams(registry.id)
      )
    );
  };

  _handleListAllSources = (): void => {
    SidepanelActions.setStorablesSidepanelActiveRegistry(null);
  };

  enhanceProps(): EnhancedProps {
    return {
      registryId: this.sidepanelStore().getStorablesSidepanelRegistryId(),
      onListRegistryFolder: this._handleListRegistryFolder,
      onListAllSources: this._handleListAllSources,
    };
  }
}

export function StorablesSidepanel(props: StorablesSidepanelProps & EnhancedProps): JSX.Element | null {
  const registryQueryResult = useSkippableBenchlingQuery<StorablesSidepanelQuery>(STORABLES_SIDEPANEL_QUERY, {
    variables: {registryId: props.registryId},
    skip: props.registryId == null,
  });

  if (props.registryId == null) {
    return (
      <RegistriesListing
        isExpanded={props.isExpanded}
        onListSource={props.onListRegistryFolder}
        sectionName="Inventory"
      />
    );
  }

  if (registryQueryResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load registry details; try reloading the page.');
    return null;
  }
  if (registryQueryResult.type === QueryResultType.PENDING) {
    return (
      <div className="sidepanel">
        <CenteredHorizontalThrobber />
      </div>
    );
  }
  const registryGraphQL = registryQueryResult.data!.registry;
  if (isInaccessible(registryGraphQL)) {
    notifications.error('You do not have permission to access this registry');
    return <></>;
  }
  return (
    <StorablesListing
      activeItemId={props.activeItemId}
      isCompactDisplay={props.isCompactDisplay}
      setIsCompactDisplay={props.setIsCompactDisplay}
      isExpanded={props.isExpanded}
      onListRegistryFolder={props.onListRegistryFolder}
      onNavigateToRoot={props.onListAllSources}
      registry={graphQLToREST('NavStoreRegistry', registryGraphQL)}
    />
  );
}

export default StorablesSidepanelConnector.connect(StorablesSidepanel);
